import type { AxiosInstance } from 'axios';
import { MediaOrientation } from '../enums/index.js';
import type * as AppApi from './interfaces/app.js';
import { transformDates } from './transformers/index.js';

export class AppClient {
  constructor(
    private authenticatedClient: AxiosInstance,
    private unauthenticatedClient?: AxiosInstance,
  ) { }

  getAppState = async (brandIdentifier: string, applicationIdentifier: string) => {
    const params = new URLSearchParams({ 
      brandIdentifier,
      applicationIdentifier,
    });
    
    const res = await this.authenticatedClient.get<AppApi.GetAppState.Response>(`/app/state?${params.toString()}`, {
      transformResponse: transformDates,
    });
    return res.data;
  };

  getChatSessionState = async (data: AppApi.GetChatSessionState.Request) => {
    const params = new URLSearchParams({ 
      applicationIdentifier: data.applicationIdentifier,
    });

    const res = await this.authenticatedClient.get<AppApi.GetChatSessionState.Response>(`/app/chat-state?${params.toString()}`);
    return res.data;
  };

  getAppTheming = async () => {
    const client = this.unauthenticatedClient || this.authenticatedClient;
    return client.get<AppApi.GetAppTheming.Response>('/app/theming')
      .then(res => res.data);
  };

  updateAppTheming = async (data: AppApi.UpdateAppTheming.Request) => {
    return this.authenticatedClient.patch<AppApi.UpdateAppTheming.Response>('/app/theming', data);
  };

  removeLogo = (data: AppApi.RemoveLogo.Request) => {
    const orientation = data.orientation === MediaOrientation.Horizontal ? 'horizontal' : 'square';

    return this.authenticatedClient.delete<AppApi.RemoveLogo.Response>(`/app/theming/logo/${orientation}`)
      .then(res => res.data);
  };

  updateLogo = (data: AppApi.UpdateLogo.Request) => {
    const formData = new FormData();
    formData.append('blob', data.blob);

    const orientation = data.orientation === MediaOrientation.Horizontal ? 'horizontal' : 'square';

    return this.authenticatedClient.post<AppApi.UpdateLogo.Response>(`/app/theming/logo/${orientation}`, formData, {
      headers: {
        'Content-Type': `multipart/form-data`,
      },
      transformRequest: () => formData,
    })
      .then(res => res.data);
  };

  updateThemeColor = (data: AppApi.UpdateThemeColor.Request) => {
    return this.authenticatedClient.put<AppApi.UpdateThemeColor.Response>(`/app/theming/color`, data)
      .then(res => res.data);
  };
}